import { useQuery } from '@tanstack/react-query'
import { qk, refreshToken } from 'api'
import constate from 'constate'
import { isRight } from 'fp-ts/lib/Either'
import { useCallback, useRef, useState } from 'react'

import {
  AccessTokenPayload,
  decodeAccessToken,
  setGlobalAccessToken,
} from './access-token'
import { useLogoutAcrossTabs } from './use-logout-across-tabs'

type NotAuthUser = {
  state: 'initial' | 'unauthenticated'
}

type AuthUser = {
  state: 'authenticated'
  accessToken: string
} & AccessTokenPayload

type User = NotAuthUser | AuthUser

const useAuth = () => {
  const [user, setUser] = useState<User>({ state: 'initial' })
  const refreshTimeoutRef = useRef<number | null>(null)

  const { refetch: refetchRefreshToken } = useQuery(
    qk.auth.token.refresh.toKey(),
    refreshToken,
    {
      staleTime: Number.POSITIVE_INFINITY,
      onSuccess: ({ accessToken }) => {
        authorize(accessToken)
      },
      onError: () => {
        unauthorize()
      },
    },
  )

  const authorize = useCallback(
    (accessToken: string) => {
      const payload = decodeAccessToken(accessToken)

      if (isRight(payload)) {
        setGlobalAccessToken(accessToken)

        setUser({
          state: 'authenticated',
          accessToken,
          ...payload.right,
        })

        const expiresIn = payload.right.exp * 1000 - Date.now()

        refreshTimeoutRef.current = window.setTimeout(() => {
          refreshTimeoutRef.current = null
          refetchRefreshToken()
        }, expiresIn)
      } else {
        setUser({ state: 'unauthenticated' })
      }
    },
    [refetchRefreshToken],
  )

  const unauthorize = useLogoutAcrossTabs(
    useCallback(() => {
      setGlobalAccessToken(null)
      setUser({ state: 'unauthenticated' })
      if (refreshTimeoutRef.current !== null) {
        window.clearTimeout(refreshTimeoutRef.current)
      }
    }, []),
  )

  return {
    user,
    authorize,
    unauthorize,
    refetchRefreshToken,
  } as const
}

export const [AuthProvider, useAuthContext] = constate(useAuth)

/**
 * Use in components that are definitely protected from unauthorized users.
 */
export const useAuthUser = () => {
  const { user } = useAuthContext()
  return user as AuthUser
}
